// Auth via the BFF (#247/#257, ADR 0047/0051/0057). The browser holds no
// token and no secret — a `__Host-bff` httpOnly cookie is the whole
// session. This module just asks the BFF who the visitor is and drives the
// browser to its `/auth/*` endpoints. Identical to storefront-ui's
// session.ts — the BFF is one shared codebase for both SPAs (ADR 0051),
// distinguished by cookie name / CloudFront origin, not by anything this
// module needs to know about.

export interface Me {
  sub: string;
  name: string;
  email?: string;
  roles: string[];
}

export type SessionState =
  | { kind: "signed-in"; me: Me }
  | { kind: "signed-out" }
  // The BFF has no Auth0 tenant configured (a fork before docs/auth0-setup.md).
  | { kind: "no-auth" }
  // Couldn't reach the BFF at all (local `vite dev` with no proxy target up).
  | { kind: "unreachable" };

/** One call on load: is there a session, and is auth even set up here? */
export async function loadSession(): Promise<SessionState> {
  let configured = true;
  try {
    const cfg = await fetch("/auth/config", { credentials: "include" });
    if (cfg.ok) {
      const body = (await cfg.json()) as { authConfigured?: boolean };
      configured = body.authConfigured !== false;
    }
  } catch {
    return { kind: "unreachable" };
  }

  try {
    const res = await fetch("/auth/me", { credentials: "include" });
    if (res.ok) {
      return { kind: "signed-in", me: (await res.json()) as Me };
    }
  } catch {
    return { kind: "unreachable" };
  }

  return configured ? { kind: "signed-out" } : { kind: "no-auth" };
}

/** True for a signed-in session carrying the `admin` role (#257's gate). */
export function isAdmin(me: Me): boolean {
  return me.roles.includes("admin");
}

export function signIn(returnUrl = "/"): void {
  window.location.assign(`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`);
}

export function signOut(returnUrl = "/"): void {
  window.location.assign(`/auth/logout?returnUrl=${encodeURIComponent(returnUrl)}`);
}
